import { useEffect, useState } from 'react';
import { IonButton } from '@ionic/react';
import { QueryParamDrivenDrawerPage } from '../../components/CareMapDrawerPage';
import { QueryParamDrivenDrawerHelper } from '../../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../../caremap/CareMapState';
import { AppPrincipal, isAnonymousViewer } from '../../auth/AppPrincipal';
import { Menu } from '../../components/Menu';
import { DeleteIcon } from '../../theme/Icons';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { StructuredRecordEditor } from './StructuredRecordEditor';
import { IStructuredRecordDrawerProps } from './StructuredRecordDrawerProps';

/**
 * Query-param drawer host for a structured record editor. A per-type provider (e.g. an
 * AllergyEditorDrawer implementing IQueryParamDrivenDrawerContentProvider) supplies its own
 * `mode` and renders this as its `Component`, passing in its descriptor + injected form-state.
 */
export const StructuredRecordEditorDrawer = <T,>({ descriptor, formState, id }: IStructuredRecordDrawerProps<T>) => {
    const mixpanel = useInstance(MixPanelEventEmitter);

    const [saving, setSaving] = useState(false);
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    useSubscription(() => formState);

    const isNew = id === descriptor.newId;
    const record = formState.state.record;

    const isViewer = !careMapState.hasPermissionTo('put');
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));
    const canAddEdit = !isViewer && !isAnonymous;

    useEffect(() => {
        if (!isNew && record) {
            mixpanel.track(`${descriptor.trackingNoun} Viewed`, () => descriptor.buildTrackingProps(record));
        }
    }, [mixpanel, isNew, record]); // eslint-disable-line react-hooks/exhaustive-deps

    const [isValid, setIsValid] = useState<boolean>(false);

    if (!careMapState.getActiveCaremap()) {
        return null;
    }

    const deleteRecord = async () => {
        setSaving(true);
        formState.delete();
        drawerHelper.close();
    };

    const save = async () => {
        if (record) {
            setSaving(true);
            formState.updateCurrentPatient();
            drawerHelper.close();
        }
    };

    const cancel = () => {
        formState.clear();
        drawerHelper.close();
    };

    const buttons = canAddEdit ? <>
        <IonButton data-type="save-button" disabled={!isValid || saving} onClick={save}>Save</IonButton>
        <IonButton data-type="cancel-button" fill="outline" onClick={cancel}>Cancel</IonButton>
    </> : <>
        <div data-type="cannot-save" />
    </>;

    return (
        <QueryParamDrivenDrawerPage
            hasBack
            buttons={buttons}
            onClose={cancel}
            data-type="structured-record-editor"
            title={`${!canAddEdit ? 'View' : isNew ? 'Add' : 'Edit'} ${descriptor.singular}`}
        >
            <>
                {(canAddEdit && !isNew) ? (
                    <div style={{marginTop: -8, display: 'flex', justifyContent: 'end'}}>
                        <Menu data-id={`edit-${descriptor.routeSegment}-menu`} actions={[{
                            Icon: DeleteIcon,
                            role: 'destructive',
                            text: `Delete ${descriptor.singular}`,
                            handler: deleteRecord,
                            'data-type': 'delete-button',
                            disabled: saving
                        }]} />
                    </div>
                ) : <div data-type="no-menu" />}

                <StructuredRecordEditor
                    descriptor={descriptor}
                    formState={formState}
                    id={id}
                    readOnly={!canAddEdit}
                    reportValidity={(valid) => setIsValid(valid)}
                />
            </>
        </QueryParamDrivenDrawerPage>
    );
};
